import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Check,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  FileText,
  BarChart3,
  ThumbsUp,
  ThumbsDown,
  Minus,
  Star,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  MapPin,
  ExternalLink,
  GripVertical,
  Award,
  X,
  Building2,
  CheckCircle,
  Sparkles,
} from 'lucide-react';
import { clsx } from 'clsx';
import { api, SearchScores } from '../../lib/api';
import { useAuthStore } from '../../stores/auth-store';

interface SearchExecutionForEval {
  id: string;
  blindCode: string;
  searchModel?: string; // Only if revealed
  status: string;
  searchResults?: {
    webResults?: Array<{
      doctorId?: string;
      type?: 'doctor' | 'institution';
      name: string;
      title?: string;
      institution?: string;
      department?: string;
      city?: string;
      canton?: string;
      specialty?: string;
      matchScore?: number;
      matchReasons?: string[];
      criteriaMatched?: string[];
      expertise?: string;
      website?: string;
      distanceFromPatient?: string;
    }>;
    searchSummary?: string;
    rankingRationale?: string;
  };
  autoScore?: {
    geographic: number;
    specialty: number;
    criteria: number;
    diversity: number;
    relevance: number;
    overall: number;
  };
}

type RunType = 'MODEL_COMPARISON' | 'MODEL_STABILITY';

interface TestRun {
  id: string;
  name: string;
  isRevealed: boolean;
  runType?: RunType;
  testCase: {
    id: string;
    name: string;
    code: string;
    condition: string;
    patientInfo: {
      age: number;
      gender: string;
      canton?: string;
    };
  };
}

const VERDICTS = [
  { value: 'EXCELLENT', label: 'Excellent', icon: Award, color: 'text-green-600 bg-green-100 border-green-200', shortcut: '1', score: 100 },
  { value: 'GOOD', label: 'Good', icon: ThumbsUp, color: 'text-blue-600 bg-blue-100 border-blue-200', shortcut: '2', score: 80 },
  { value: 'ADEQUATE', label: 'Adequate', icon: Check, color: 'text-amber-600 bg-amber-100 border-amber-200', shortcut: '3', score: 60 },
  { value: 'POOR', label: 'Poor', icon: Minus, color: 'text-orange-600 bg-orange-100 border-orange-200', shortcut: '4', score: 40 },
  { value: 'INADEQUATE', label: 'Inadequate', icon: ThumbsDown, color: 'text-red-600 bg-red-100 border-red-200', shortcut: '5', score: 20 },
];

// Helper to get verdict score for auto-ranking
const getVerdictScore = (verdict?: string): number => {
  const found = VERDICTS.find((v) => v.value === verdict);
  return found?.score ?? -1;
};

// Helper to get verdict config
const getVerdictConfig = (verdict?: string) => {
  return VERDICTS.find((v) => v.value === verdict);
};

type DoctorResult = NonNullable<NonNullable<SearchExecutionForEval['searchResults']>['webResults']>[number];

function DoctorDetailModal({ doctor, index, onClose }: { doctor: DoctorResult; index: number; onClose: () => void }) {
  const matchScore = doctor.matchScore ?? 0;
  const scoreColor = matchScore >= 90 ? '#22c55e' : matchScore >= 75 ? '#3b82f6' : matchScore >= 60 ? '#eab308' : '#9ca3af';
  const scoreTextColor = matchScore >= 90 ? 'text-green-600' : matchScore >= 75 ? 'text-blue-600' : matchScore >= 60 ? 'text-yellow-600' : 'text-gray-600';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="relative p-5 border-b border-gray-100">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="flex items-start gap-4 pr-10">
            {/* Score Ring */}
            <div className="relative flex-shrink-0">
              {matchScore > 0 ? (
                <>
                  <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                    <circle
                      cx="18" cy="18" r="15.5"
                      fill="none"
                      stroke={scoreColor}
                      strokeWidth="3"
                      strokeDasharray={`${matchScore} 100`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={clsx('text-lg font-bold', scoreTextColor)}>{matchScore}</span>
                  </div>
                </>
              ) : (
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
                  <span className="text-2xl font-bold text-amber-700">#{index + 1}</span>
                </div>
              )}
            </div>

            {/* Doctor Info */}
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-semibold text-gray-900 leading-tight">
                {doctor.title && <span className="text-gray-500">{doctor.title} </span>}
                {doctor.name}
              </h2>
              {doctor.specialty && (
                <p className="text-sm text-amber-600 font-medium mt-0.5">{doctor.specialty}</p>
              )}
              {doctor.institution && (
                <div className="flex items-center gap-1.5 mt-2 text-sm text-gray-600">
                  <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="truncate">{doctor.institution}{doctor.department && ` - ${doctor.department}`}</span>
                </div>
              )}
              {(doctor.city || doctor.canton) && (
                <div className="flex items-center gap-1.5 mt-1 text-sm text-gray-500">
                  <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span>{doctor.city}{doctor.city && doctor.canton ? ', ' : ''}{doctor.canton}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-5 overflow-y-auto max-h-[calc(90vh-200px)] space-y-4">
          {/* Match Reasons */}
          {doctor.matchReasons && doctor.matchReasons.length > 0 && (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 rounded-lg bg-blue-100">
                  <Award className="h-4 w-4 text-blue-600" />
                </div>
                <span className="text-sm font-semibold text-blue-900">Why This Doctor Matches</span>
              </div>
              <ul className="space-y-2">
                {doctor.matchReasons.map((reason, i) => (
                  <li key={i} className="text-sm text-blue-800 flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-500" />
                    <span>{reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Matching Criteria Matched - Always show this section */}
          <div>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Matching Criteria Matched</span>
            {doctor.criteriaMatched && doctor.criteriaMatched.length > 0 ? (
              <div className="flex flex-wrap gap-2 mt-2">
                {doctor.criteriaMatched.map((criterion, i) => (
                  <span
                    key={i}
                    className="px-3 py-1.5 text-xs bg-green-100 text-green-700 rounded-full font-medium border border-green-200"
                  >
                    {criterion}
                  </span>
                ))}
              </div>
            ) : (
              <p className="mt-2 text-sm text-gray-400 italic">No criteria explicitly matched by this model</p>
            )}
          </div>

          {/* Expertise */}
          {doctor.expertise && (
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Expertise</span>
              <p className="mt-2 text-sm text-gray-700 leading-relaxed">{doctor.expertise}</p>
            </div>
          )}

          {/* No additional info message - only show if matchReasons and expertise are both missing */}
          {!doctor.matchReasons?.length && !doctor.expertise && !doctor.institution && (
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 text-center">
              <p className="text-sm text-gray-500">Limited details available from this model's search results.</p>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
          <span className="text-xs text-gray-400">Result #{index + 1}</span>
          {doctor.website && (
            <a
              href={doctor.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium shadow-sm"
            >
              <ExternalLink className="h-4 w-4" />
              Visit Website
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function DoctorResultCard({ doctor, index, onClick }: { doctor: DoctorResult; index: number; onClick: () => void }) {
  const matchScore = doctor.matchScore ?? 0;
  const scoreColor = matchScore >= 90 ? 'text-green-600' : matchScore >= 75 ? 'text-blue-600' : matchScore >= 60 ? 'text-yellow-600' : 'text-gray-500';
  const criteriaToShow = doctor.criteriaMatched?.slice(0, 2) || [];
  const remainingCriteria = (doctor.criteriaMatched?.length || 0) - 2;

  return (
    <button
      onClick={onClick}
      className="p-4 bg-white rounded-lg border border-gray-200 h-full flex flex-col text-left hover:border-amber-300 hover:shadow-md transition-all cursor-pointer w-full"
    >
      {/* Header with rank, name, and score */}
      <div className="flex items-start gap-2">
        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">
          {index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="font-semibold text-gray-900 text-sm leading-tight line-clamp-2">{doctor.name}</p>
            {matchScore > 0 && (
              <span className={clsx('text-sm font-bold flex-shrink-0', scoreColor)}>{matchScore}%</span>
            )}
          </div>
          {doctor.specialty && (
            <p className="text-xs text-amber-600 font-medium mt-0.5 truncate">{doctor.specialty}</p>
          )}
        </div>
      </div>

      {/* Institution and location */}
      <div className="mt-2 space-y-1 text-xs text-gray-500">
        {doctor.institution && (
          <p className="truncate">{doctor.institution}{doctor.city && ` • ${doctor.city}`}{doctor.canton && `, ${doctor.canton}`}</p>
        )}
        {!doctor.institution && (doctor.city || doctor.canton) && (
          <div className="flex items-center gap-1">
            <MapPin className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{[doctor.city, doctor.canton].filter(Boolean).join(', ')}</span>
          </div>
        )}
      </div>

      {/* Expertise preview */}
      {doctor.expertise && (
        <p className="mt-2 text-xs text-gray-600 line-clamp-2">{doctor.expertise}</p>
      )}

      {/* Criteria matched badges */}
      {criteriaToShow.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {criteriaToShow.map((criterion, i) => (
            <span
              key={i}
              className="px-2 py-0.5 text-[10px] bg-green-100 text-green-700 rounded-full font-medium truncate max-w-[150px]"
            >
              {criterion}
            </span>
          ))}
          {remainingCriteria > 0 && (
            <span className="px-2 py-0.5 text-[10px] bg-gray-100 text-gray-600 rounded-full font-medium">
              +{remainingCriteria}
            </span>
          )}
        </div>
      )}
    </button>
  );
}

// Auto-Ranking Section for Phase 2 Search Evaluation
function SearchAutoRankingSection({
  executions,
  evaluations,
  rankings,
  selectedBest,
  onRankingsChange,
  onSelectedBestChange,
  isReadOnly = false,
}: {
  executions: SearchExecutionForEval[];
  evaluations: Record<string, { verdict: string; scores?: unknown; note?: string; strengths?: string[]; weaknesses?: string[] }>;
  rankings: string[];
  selectedBest: string | null;
  onRankingsChange: (newRankings: string[]) => void;
  onSelectedBestChange: (blindCode: string) => void;
  isReadOnly?: boolean;
}) {
  const allVerdictsComplete = executions.every((e) => evaluations[e.blindCode]?.verdict);

  // Calculate auto-ranking based on verdict scores
  const rankedExecutions = executions
    .map((e) => ({
      blindCode: e.blindCode,
      verdict: evaluations[e.blindCode]?.verdict,
      score: getVerdictScore(evaluations[e.blindCode]?.verdict),
      autoScore: e.autoScore?.overall ?? 0,
    }))
    .sort((a, b) => {
      // Sort by verdict score first, then by auto-score as tiebreaker
      if (b.score !== a.score) return b.score - a.score;
      return b.autoScore - a.autoScore;
    });

  // Get current ranking order
  const getCurrentOrder = (): string[] => {
    if (rankings.length === executions.length) {
      return rankings;
    }
    return rankedExecutions.map((e) => e.blindCode);
  };

  const currentOrder = getCurrentOrder();

  // Apply auto-ranking to state
  const handleApplyAutoRanking = () => {
    const newOrder = rankedExecutions.map((e) => e.blindCode);
    onRankingsChange(newOrder);
    // Auto-select the best (first in auto-ranking)
    if (newOrder.length > 0) {
      onSelectedBestChange(newOrder[0]);
    }
  };

  // Move execution up in ranking
  const handleMoveUp = (blindCode: string) => {
    const currentIdx = currentOrder.indexOf(blindCode);
    if (currentIdx <= 0) return;

    const newOrder = [...currentOrder];
    [newOrder[currentIdx - 1], newOrder[currentIdx]] = [newOrder[currentIdx], newOrder[currentIdx - 1]];
    onRankingsChange(newOrder);
  };

  // Move execution down in ranking
  const handleMoveDown = (blindCode: string) => {
    const currentIdx = currentOrder.indexOf(blindCode);
    if (currentIdx >= currentOrder.length - 1) return;

    const newOrder = [...currentOrder];
    [newOrder[currentIdx], newOrder[currentIdx + 1]] = [newOrder[currentIdx + 1], newOrder[currentIdx]];
    onRankingsChange(newOrder);
  };

  if (!allVerdictsComplete) {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
        <div className="flex items-center gap-3 text-gray-500">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200">
            <BarChart3 className="h-5 w-5 text-gray-400" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-700">Matching Ranking</h3>
            <p className="text-sm">Complete all verdicts above to see the auto-generated ranking</p>
          </div>
        </div>
        {/* Progress indicator */}
        <div className="mt-4 flex items-center gap-2">
          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-400 rounded-full transition-all"
              style={{ width: `${(Object.values(evaluations).filter((e) => e.verdict).length / executions.length) * 100}%` }}
            />
          </div>
          <span className="text-xs text-gray-500">
            {Object.values(evaluations).filter((e) => e.verdict).length}/{executions.length} verdicts
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
            <BarChart3 className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Matching Ranking</h3>
            <p className="text-xs text-gray-500">
              {isReadOnly ? 'Recorded ranking' : 'Auto-generated from verdicts • Adjust with arrows if needed'}
            </p>
          </div>
        </div>
        {!isReadOnly && (
          <button
            onClick={handleApplyAutoRanking}
            className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-white px-3 py-1.5 text-sm font-medium text-amber-700 hover:bg-amber-50 transition-colors"
          >
            <Sparkles className="h-4 w-4" />
            Reset to Auto
          </button>
        )}
      </div>

      {/* Ranking List */}
      <div className="p-4">
        <div className="space-y-2">
          {currentOrder.map((blindCode, index) => {
            const verdict = evaluations[blindCode]?.verdict;
            const verdictConfig = getVerdictConfig(verdict);
            const score = getVerdictScore(verdict);
            const execution = executions.find((e) => e.blindCode === blindCode);
            const autoScore = execution?.autoScore?.overall;
            const isFirst = index === 0;
            const isLast = index === currentOrder.length - 1;
            const isBest = selectedBest === blindCode;

            return (
              <div
                key={blindCode}
                className={clsx(
                  'flex items-center gap-3 p-3 rounded-lg border transition-all',
                  isBest && 'bg-amber-50 border-amber-300 ring-2 ring-amber-200',
                  !isBest && index === 0 && 'bg-amber-50/50 border-amber-200',
                  !isBest && index === 1 && 'bg-gray-50 border-gray-200',
                  !isBest && index === 2 && 'bg-orange-50/50 border-orange-200/50',
                  !isBest && index > 2 && 'bg-white border-gray-200'
                )}
              >
                {/* Rank Badge */}
                <div
                  className={clsx(
                    'flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold shrink-0',
                    isBest && 'bg-amber-500 text-white',
                    !isBest && index === 0 && 'bg-amber-400 text-white',
                    !isBest && index === 1 && 'bg-gray-400 text-white',
                    !isBest && index === 2 && 'bg-orange-400 text-white',
                    !isBest && index > 2 && 'bg-gray-200 text-gray-600'
                  )}
                >
                  {index + 1}
                </div>

                {/* Grip indicator */}
                <GripVertical className="h-4 w-4 text-gray-300" />

                {/* Blind Code */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold text-gray-900">{blindCode}</span>
                    {verdictConfig && (
                      <span
                        className={clsx(
                          'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
                          verdictConfig.color
                        )}
                      >
                        <verdictConfig.icon className="h-3 w-3" />
                        {verdictConfig.label}
                      </span>
                    )}
                    {isBest && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-500 text-white">
                        <Award className="h-3 w-3" />
                        Best
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    Verdict: {score} pts
                    {autoScore != null && ` • Auto-score: ${autoScore.toFixed(1)}`}
                  </p>
                </div>

                {/* Move Buttons */}
                {!isReadOnly && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleMoveUp(blindCode)}
                      disabled={isFirst}
                      className={clsx(
                        'p-1.5 rounded-md transition-colors',
                        isFirst
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      )}
                      title="Move up"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleMoveDown(blindCode)}
                      disabled={isLast}
                      className={clsx(
                        'p-1.5 rounded-md transition-colors',
                        isLast
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      )}
                      title="Move down"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {/* Select as Best Button */}
                {!isReadOnly ? (
                  <button
                    onClick={() => onSelectedBestChange(blindCode)}
                    className={clsx(
                      'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                      isBest
                        ? 'bg-amber-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-amber-100 hover:text-amber-700'
                    )}
                  >
                    {isBest ? '★ Best' : 'Select Best'}
                  </button>
                ) : (
                  isBest && (
                    <span className="px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-500 text-white">
                      ★ Best
                    </span>
                  )
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-amber-500" />
            {isReadOnly
              ? 'Rankings were calculated from verdicts.'
              : 'Rankings auto-calculated from verdicts. Select the best result and adjust order if clinical judgment differs.'}
          </p>
        </div>
      </div>
    </div>
  );
}


function SearchExecutionCard({
  execution,
  verdict,
  note,
  strengths,
  weaknesses,
  onVerdictChange,
  onNoteChange,
  onStrengthsChange,
  onWeaknessesChange,
  isReadOnly = false,
  hideEvaluationControls = false,
}: {
  execution: SearchExecutionForEval;
  verdict?: string;
  note?: string;
  strengths: string[];
  weaknesses: string[];
  onVerdictChange: (verdict: string) => void;
  onNoteChange: (note: string) => void;
  onStrengthsChange: (strengths: string[]) => void;
  onWeaknessesChange: (weaknesses: string[]) => void;
  isReadOnly?: boolean;
  hideEvaluationControls?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [newStrength, setNewStrength] = useState('');
  const [newWeakness, setNewWeakness] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState<{ doctor: DoctorResult; index: number } | null>(null);

  const results = execution.searchResults?.webResults || [];

  const addStrength = () => {
    if (newStrength.trim()) {
      onStrengthsChange([...strengths, newStrength.trim()]);
      setNewStrength('');
    }
  };

  const addWeakness = () => {
    if (newWeakness.trim()) {
      onWeaknessesChange([...weaknesses, newWeakness.trim()]);
      setNewWeakness('');
    }
  };

  const removeStrength = (index: number) => {
    onStrengthsChange(strengths.filter((_, i) => i !== index));
  };

  const removeWeakness = (index: number) => {
    onWeaknessesChange(weaknesses.filter((_, i) => i !== index));
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
            <span className="font-mono text-lg font-bold text-amber-700">
              {execution.blindCode.slice(0, 1)}
            </span>
          </div>
          <div>
            <h3 className="font-mono font-semibold text-gray-900">{execution.blindCode}</h3>
            <p className="text-xs text-gray-500">
              {results.length} care providers found • Ready for evaluation
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {execution.autoScore?.overall != null && (
            <div className="text-right">
              <span className="text-xs text-gray-500">Auto-Score</span>
              <p className="text-lg font-bold text-amber-600">{execution.autoScore.overall.toFixed(1)}</p>
            </div>
          )}
          <button
            onClick={() => setExpanded(!expanded)}
            className="rounded p-1 hover:bg-gray-200"
          >
            {expanded ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>
        </div>
      </div>

      {expanded && (
        <>
          {/* Search Results */}
          <div className="border-b border-gray-100 p-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Matching Results ({results.length})</h4>
            {results.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 max-h-96 overflow-y-auto">
                {results.map((doctor, idx) => (
                  <DoctorResultCard
                    key={idx}
                    doctor={doctor}
                    index={idx}
                    onClick={() => setSelectedDoctor({ doctor, index: idx })}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <AlertCircle className="h-8 w-8 mx-auto text-gray-300" />
                {execution.searchModel === 'BETTERDOC' ? (
                  <p className="mt-2 text-sm font-medium">No results found with BetterDoc, manual search needed</p>
                ) : (
                  <>
                    <p className="mt-2 text-sm font-medium">No results found</p>
                    {/* Show explanation for why no results */}
                    {(execution.searchResults?.rankingRationale || execution.searchResults?.searchSummary) && (
                      <p className="mt-2 text-xs text-gray-400 max-w-md mx-auto">
                        {execution.searchResults.rankingRationale || execution.searchResults.searchSummary}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Doctor Detail Modal */}
          {selectedDoctor && (
            <DoctorDetailModal
              doctor={selectedDoctor.doctor}
              index={selectedDoctor.index}
              onClose={() => setSelectedDoctor(null)}
            />
          )}

          {/* Evaluation Controls - hidden when no evaluation exists in read-only mode */}
          {!hideEvaluationControls && (
          <div className="bg-gray-50 p-6 space-y-6">
            {/* Verdict Selection */}
            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700">
                <Star className="h-4 w-4" />
                Overall Verdict
              </label>
              <div className="flex flex-wrap gap-2">
                {VERDICTS.map((v) => {
                  const Icon = v.icon;
                  return (
                    <button
                      key={v.value}
                      type="button"
                      onClick={() => !isReadOnly && onVerdictChange(v.value)}
                      disabled={isReadOnly}
                      className={clsx(
                        'flex items-center gap-2 rounded-lg border-2 px-3 py-2 text-sm font-medium transition-all',
                        verdict === v.value
                          ? v.color + ' ring-2 ring-offset-2'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300',
                        isReadOnly && 'cursor-not-allowed opacity-75'
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      {v.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Strengths and Weaknesses */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-green-700">
                  <ThumbsUp className="h-4 w-4" />
                  Strengths
                </label>
                <div className="space-y-2">
                  {strengths.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm bg-green-50 text-green-700 rounded px-2 py-1">
                      <span className="flex-1">{s}</span>
                      {!isReadOnly && (
                        <button onClick={() => removeStrength(i)} className="text-green-500 hover:text-green-700">
                          <Minus className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  {!isReadOnly && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newStrength}
                      onChange={(e) => setNewStrength(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addStrength()}
                      placeholder="Add a strength..."
                      className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm focus:border-green-500 focus:outline-none"
                    />
                    <button
                      onClick={addStrength}
                      className="rounded bg-green-100 px-2 py-1 text-green-700 hover:bg-green-200"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  </div>
                  )}
                  {isReadOnly && strengths.length === 0 && (
                    <p className="text-sm text-gray-500 italic">No strengths recorded</p>
                  )}
                </div>
              </div>
              <div>
                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-red-700">
                  <ThumbsDown className="h-4 w-4" />
                  Weaknesses
                </label>
                <div className="space-y-2">
                  {weaknesses.map((w, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm bg-red-50 text-red-700 rounded px-2 py-1">
                      <span className="flex-1">{w}</span>
                      {!isReadOnly && (
                        <button onClick={() => removeWeakness(i)} className="text-red-500 hover:text-red-700">
                          <Minus className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  {!isReadOnly && (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newWeakness}
                        onChange={(e) => setNewWeakness(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addWeakness()}
                        placeholder="Add a weakness..."
                        className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm focus:border-red-500 focus:outline-none"
                      />
                      <button
                        onClick={addWeakness}
                        className="rounded bg-red-100 px-2 py-1 text-red-700 hover:bg-red-200"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  {isReadOnly && weaknesses.length === 0 && (
                    <p className="text-sm text-gray-500 italic">No weaknesses recorded</p>
                  )}
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700">
                <MessageSquare className="h-4 w-4" />
                Notes
              </label>
              <textarea
                value={note || ''}
                onChange={(e) => !isReadOnly && onNoteChange(e.target.value)}
                rows={2}
                disabled={isReadOnly}
                placeholder={isReadOnly ? 'No notes recorded' : 'Add notes about this search execution...'}
                className={clsx(
                  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500',
                  isReadOnly ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'
                )}
              />
            </div>
          </div>
          )}
        </>
      )}
    </div>
  );
}

export function SearchBlindEvaluation() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [evaluations, setEvaluations] = useState<
    Record<
      string,
      {
        verdict: string;
        scores: SearchScores;
        note: string;
        strengths: string[];
        weaknesses: string[];
      }
    >
  >({});
  const [rankings, setRankings] = useState<string[]>([]);
  const [selectedBest, setSelectedBest] = useState<string | null>(null);
  const [selectionReasoning, setSelectionReasoning] = useState('');
  const [evaluatorNotes, setEvaluatorNotes] = useState('');
  const [confidence, setConfidence] = useState(75);

  // Get current user for matching evaluation
  const user = useAuthStore((state) => state.user);
  const currentUserEmail = user?.username;

  // Fetch search results for evaluation
  const { data: resultsData, isLoading: resultsLoading } = useQuery({
    queryKey: ['validation', 'search-results', id],
    queryFn: () => api.searchPhase.getResults(id!),
    enabled: !!id,
  });

  // Fetch test run info
  const { data: testRunData, isLoading: testRunLoading } = useQuery({
    queryKey: ['validation', 'test-run', id],
    queryFn: () => api.get<{
      data: TestRun & {
        searchEvaluationSets: Array<{
          id: string;
          evaluator: { id: string; name: string; email: string };
          rankings: string[];
          selectedBest: string | null;
          evaluatorNotes: string | null;
          evaluatorConfidence: number | null;
          selectionReasoning: string | null;
          completedAt: string | null;
          items: Array<{
            blindCode: string;
            verdict: string;
            verdictReasoning?: string;
            scores?: SearchScores;
            strengths?: string[];
            weaknesses?: string[];
          }>;
        }>;
      };
    }>(`/api/validation/test-runs/${id}`),
    enabled: !!id,
  });

  // Find current user's existing search evaluation
  const existingSearchEvaluation = testRunData?.data?.searchEvaluationSets?.find(
    (evalSet) => evalSet.evaluator.email === currentUserEmail && evalSet.completedAt
  );

  // Check if we're in read-only mode (only if revealed AND has existing evaluation)
  const testRun = testRunData?.data;
  const hasExistingEvaluation = !!existingSearchEvaluation;
  const isReadOnly = (testRun?.isRevealed ?? false) && hasExistingEvaluation;

  // Create or get evaluation set
  const createEvalMutation = useMutation({
    mutationFn: () => api.searchEvaluations.create(id!),
  });

  const evaluationSetId = createEvalMutation.data?.data?.id;

  const submitMutation = useMutation({
    mutationFn: (data: Parameters<typeof api.searchEvaluations.complete>[2]) => {
      if (!evaluationSetId) {
        return Promise.reject(new Error('Evaluation set not ready. Please wait or refresh the page.'));
      }
      return api.searchEvaluations.complete(id!, evaluationSetId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-run', id] });
      queryClient.invalidateQueries({ queryKey: ['validation', 'search-phase', id] });
      navigate(`/validation/test-runs/${id}`);
    },
  });

  const isLoading = resultsLoading || testRunLoading;
  const searchExecutions = (resultsData?.data?.searchExecutions || []).filter(
    (e) => e.status === 'COMPLETED'
  ) as SearchExecutionForEval[];

  // Initialize rankings from presentation order
  useEffect(() => {
    if (searchExecutions.length > 0 && rankings.length === 0) {
      setRankings(searchExecutions.map((e) => e.blindCode));
    }
  }, [searchExecutions, rankings.length]);

  // Create evaluation set on mount if needed (only if not read-only)
  useEffect(() => {
    if (id && !isReadOnly && !createEvalMutation.data && !createEvalMutation.isPending) {
      createEvalMutation.mutate();
    }
  }, [id, isReadOnly]);

  // Pre-populate form with existing evaluation data
  useEffect(() => {
    if (existingSearchEvaluation) {
      // Populate evaluations from existing items
      const evalMap: Record<string, {
        verdict: string;
        scores: SearchScores;
        note: string;
        strengths: string[];
        weaknesses: string[];
      }> = {};

      existingSearchEvaluation.items.forEach((item) => {
        evalMap[item.blindCode] = {
          verdict: item.verdict,
          scores: item.scores || {
            relevance: { overallRelevance: 3, specialtyMatch: 3, criteriaAlignment: 3, geographicFit: 3 },
            quality: { resultDiversity: 3, informationCompleteness: 3, sourceQuality: 3, clinicalRelevance: 3 },
            completeness: { requiredFieldsCovered: 3, optionalFieldsCovered: 3, verifiableInformation: 3 },
          },
          note: item.verdictReasoning || '',
          strengths: item.strengths || [],
          weaknesses: item.weaknesses || [],
        };
      });
      setEvaluations(evalMap);

      // Populate rankings
      if (existingSearchEvaluation.rankings && existingSearchEvaluation.rankings.length > 0) {
        setRankings(existingSearchEvaluation.rankings);
      }

      // Populate selected best
      if (existingSearchEvaluation.selectedBest) {
        setSelectedBest(existingSearchEvaluation.selectedBest);
      }

      // Populate reasoning and notes
      if (existingSearchEvaluation.selectionReasoning) {
        setSelectionReasoning(existingSearchEvaluation.selectionReasoning);
      }
      if (existingSearchEvaluation.evaluatorNotes) {
        setEvaluatorNotes(existingSearchEvaluation.evaluatorNotes);
      }
      if (existingSearchEvaluation.evaluatorConfidence) {
        setConfidence(existingSearchEvaluation.evaluatorConfidence);
      }
    }
  }, [existingSearchEvaluation]);

  const updateEvaluation = (
    blindCode: string,
    field: keyof (typeof evaluations)[string],
    value: unknown
  ) => {
    setEvaluations((prev) => ({
      ...prev,
      [blindCode]: {
        ...prev[blindCode],
        verdict: prev[blindCode]?.verdict || '',
        scores: prev[blindCode]?.scores || {
          relevance: { overallRelevance: 3, specialtyMatch: 3, criteriaAlignment: 3, geographicFit: 3 },
          quality: { resultDiversity: 3, informationCompleteness: 3, sourceQuality: 3, clinicalRelevance: 3 },
          completeness: { requiredFieldsCovered: 3, optionalFieldsCovered: 3, verifiableInformation: 3 },
        },
        note: prev[blindCode]?.note || '',
        strengths: prev[blindCode]?.strengths || [],
        weaknesses: prev[blindCode]?.weaknesses || [],
        [field]: value,
      },
    }));
  };

  const handleSubmit = () => {
    // Validate all executions have verdicts
    const allVerdictsSet = searchExecutions.every((e) => evaluations[e.blindCode]?.verdict);
    if (!allVerdictsSet) {
      alert('Please provide a verdict for all search executions');
      return;
    }

    // Auto-generate rankings from verdicts if not already set
    let finalRankings = rankings;
    let finalSelectedBest = selectedBest;

    if (rankings.length !== searchExecutions.length) {
      // Generate rankings from verdict scores
      const rankedByScore = searchExecutions
        .map((e) => ({
          blindCode: e.blindCode,
          score: getVerdictScore(evaluations[e.blindCode]?.verdict),
          autoScore: e.autoScore?.overall ?? 0,
        }))
        .sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return b.autoScore - a.autoScore;
        });

      finalRankings = rankedByScore.map((item) => item.blindCode);
    }

    // Auto-select best if not selected
    if (!finalSelectedBest && finalRankings.length > 0) {
      finalSelectedBest = finalRankings[0];
    }

    if (!finalSelectedBest) {
      alert('Please select the best search result');
      return;
    }

    // Prepare items for submission
    const items = searchExecutions.map((exec) => {
      const eval_ = evaluations[exec.blindCode];
      return {
        blindCode: exec.blindCode,
        verdict: eval_?.verdict || 'ADEQUATE',
        verdictReasoning: eval_?.note,
        scores: eval_?.scores || {
          relevance: { overallRelevance: 3, specialtyMatch: 3, criteriaAlignment: 3, geographicFit: 3 },
          quality: { resultDiversity: 3, informationCompleteness: 3, sourceQuality: 3, clinicalRelevance: 3 },
          completeness: { requiredFieldsCovered: 3, optionalFieldsCovered: 3, verifiableInformation: 3 },
        },
        strengths: eval_?.strengths || [],
        weaknesses: eval_?.weaknesses || [],
      };
    });

    submitMutation.mutate({
      items,
      rankings: finalRankings,
      selectedBest: finalSelectedBest,
      selectionReasoning: selectionReasoning.trim() || undefined,
      evaluatorConfidence: confidence,
      evaluatorNotes: evaluatorNotes.trim() || undefined,
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (!testRun || searchExecutions.length === 0) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-8 text-center">
        <AlertCircle className="mx-auto h-10 w-10 text-amber-500" />
        <h2 className="mt-4 text-lg font-semibold text-gray-900">No Matching Results</h2>
        <p className="mt-1 text-sm text-gray-500">
          Complete Phase 4: Care Provider Matching before starting evaluation.
        </p>
        <Link
          to={`/validation/test-runs/${id}`}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Test Run
        </Link>
      </div>
    );
  }

  // If revealed, continue to render in read-only mode (whether or not evaluation exists)

  const completedCount = Object.values(evaluations).filter((e) => e.verdict).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate(`/validation/test-runs/${id}`)}
            className="rounded-lg p-2 hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Matching Evaluation</h1>
            <p className="text-sm text-gray-500">{testRun.name} - Phase 2</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isReadOnly ? (
            <>
              <Eye className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-green-600">Revealed (Read Only)</span>
            </>
          ) : testRun?.isRevealed ? (
            <>
              <Eye className="h-5 w-5 text-amber-600" />
              <span className="text-sm font-medium text-amber-600">Revealed Mode</span>
            </>
          ) : (
            <>
              <EyeOff className="h-5 w-5 text-amber-600" />
              <span className="text-sm font-medium text-amber-600">Blind Mode</span>
            </>
          )}
        </div>
      </div>

      {/* Notice when evaluating after reveal (not blind) */}
      {testRun?.isRevealed && !hasExistingEvaluation && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-3">
            <Eye className="h-5 w-5 text-amber-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">Results have been revealed</p>
              <p className="text-xs text-amber-600 mt-0.5">
                You can still submit an evaluation, but model identities are now visible.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Context */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="h-5 w-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Test Case Context</h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <span className="text-xs text-gray-500">Test Case</span>
            <p className="font-medium text-gray-900">{testRun.testCase.name}</p>
          </div>
          <div>
            <span className="text-xs text-gray-500">Condition</span>
            <p className="text-sm text-gray-900">{testRun.testCase.condition}</p>
          </div>
          <div>
            <span className="text-xs text-gray-500">Patient</span>
            <p className="text-sm text-gray-900">
              {testRun.testCase.patientInfo.age} y/o {testRun.testCase.patientInfo.gender}
              {testRun.testCase.patientInfo.canton && ` - ${testRun.testCase.patientInfo.canton}`}
            </p>
          </div>
        </div>

        {/* ICD-10 Codes (from indication analysis) */}
        {resultsData?.testCase?.icd10Codes && (
          resultsData.testCase.icd10Codes.leadingCondition ||
          (Array.isArray(resultsData.testCase.icd10Codes.otherConditions) && resultsData.testCase.icd10Codes.otherConditions.length > 0)
        ) && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <span className="text-[10px] font-medium text-blue-600 uppercase tracking-wide flex items-center gap-1">
              <FileText className="h-3 w-3" />
              ICD-10 Codes (for BetterDoc)
            </span>
            <div className="mt-2 flex flex-wrap gap-2">
              {resultsData.testCase.icd10Codes.leadingCondition && (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-mono">
                  <span className="text-[9px] text-blue-600 font-medium bg-blue-200 px-1 rounded">PRIMARY</span>
                  {resultsData.testCase.icd10Codes.leadingCondition}
                  {resultsData.testCase.icd10Codes.leadingConditionName && (
                    <span className="text-blue-600 font-normal">({resultsData.testCase.icd10Codes.leadingConditionName})</span>
                  )}
                </span>
              )}
              {Array.isArray(resultsData.testCase.icd10Codes.otherConditions) &&
                resultsData.testCase.icd10Codes.otherConditions.map((condition, i) => {
                  // Handle both string[] and Array<{code, name}> formats
                  const code = typeof condition === 'string' ? condition : condition.code;
                  const name = typeof condition === 'string' ? undefined : condition.name;
                  return (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-mono">
                      {code}
                      {name && <span className="text-gray-500 font-normal">({name})</span>}
                    </span>
                  );
                })}
            </div>
          </div>
        )}
      </div>

      {/* Search Executions */}
      <div className="space-y-6">
        {searchExecutions.map((execution) => (
          <SearchExecutionCard
            key={execution.id}
            execution={execution}
            verdict={evaluations[execution.blindCode]?.verdict}
            note={evaluations[execution.blindCode]?.note}
            strengths={evaluations[execution.blindCode]?.strengths || []}
            weaknesses={evaluations[execution.blindCode]?.weaknesses || []}
            onVerdictChange={(v) => updateEvaluation(execution.blindCode, 'verdict', v)}
            onNoteChange={(n) => updateEvaluation(execution.blindCode, 'note', n)}
            onStrengthsChange={(s) => updateEvaluation(execution.blindCode, 'strengths', s)}
            onWeaknessesChange={(w) => updateEvaluation(execution.blindCode, 'weaknesses', w)}
            isReadOnly={isReadOnly}
            hideEvaluationControls={false}
          />
        ))}
      </div>

      {/* Auto-Ranking Section */}
      <SearchAutoRankingSection
        executions={searchExecutions}
        evaluations={evaluations}
        rankings={rankings}
        selectedBest={selectedBest}
        onRankingsChange={setRankings}
        onSelectedBestChange={setSelectedBest}
        isReadOnly={isReadOnly}
      />

      {/* Selection Reasoning & Notes */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 space-y-4">
          <div>
            <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700">
              <Award className="h-4 w-4" />
              Why is {selectedBest || 'this'} the best?
            </label>
            <textarea
              value={selectionReasoning}
              onChange={(e) => !isReadOnly && setSelectionReasoning(e.target.value)}
              rows={2}
              disabled={isReadOnly}
              placeholder={isReadOnly ? 'No reasoning recorded' : 'Explain your selection...'}
              className={clsx(
                'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500',
                isReadOnly ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'
              )}
            />
          </div>

          <div>
            <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700">
              <Star className="h-4 w-4" />
              Confidence Level: {confidence}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={confidence}
              disabled={isReadOnly}
              onChange={(e) => !isReadOnly && setConfidence(Number(e.target.value))}
              className={clsx(
                'w-full accent-amber-600',
                isReadOnly && 'cursor-not-allowed opacity-75'
              )}
            />
          </div>

          <div>
            <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700">
              <MessageSquare className="h-4 w-4" />
              Overall Evaluation Notes
            </label>
            <textarea
              value={evaluatorNotes}
              onChange={(e) => !isReadOnly && setEvaluatorNotes(e.target.value)}
              rows={2}
              disabled={isReadOnly}
              placeholder={isReadOnly ? 'No notes recorded' : 'Any additional notes...'}
              className={clsx(
                'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500',
                isReadOnly ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'
              )}
            />
          </div>
        </div>

      {/* Footer */}
      <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-6">
        <div className="text-sm text-gray-600">
          {completedCount === searchExecutions.length ? (
            <span className="flex items-center gap-2 text-green-600">
              <Check className="h-4 w-4" />
              {isReadOnly
                ? `All ${searchExecutions.length} verdicts recorded`
                : `All ${searchExecutions.length} verdicts complete${selectedBest ? ' • Best selected' : ' • Rankings auto-ready'}`}
            </span>
          ) : (
            <span>
              {completedCount} of {searchExecutions.length} verdicts {isReadOnly ? 'recorded' : 'complete'}
            </span>
          )}
        </div>
        {!isReadOnly && (
          <button
            onClick={handleSubmit}
            disabled={
              submitMutation.isPending ||
              createEvalMutation.isPending ||
              !evaluationSetId ||
              completedCount !== searchExecutions.length
            }
            className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitMutation.isPending || createEvalMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            {createEvalMutation.isPending ? 'Preparing...' : 'Submit Evaluation'}
            <ArrowRight className="h-4 w-4" />
          </button>
        )}
        {isReadOnly && (
          <Link
            to={`/validation/test-runs/${id}/results`}
            className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-6 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            <BarChart3 className="h-4 w-4" />
            View Results
          </Link>
        )}
      </div>

      {/* Error Display */}
      {(submitMutation.isError || createEvalMutation.isError) && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">Error</span>
          </div>
          <p className="mt-1 text-sm text-red-600">
            {(submitMutation.error || createEvalMutation.error) instanceof Error
              ? (submitMutation.error || createEvalMutation.error)?.message
              : 'An unexpected error occurred'}
          </p>
        </div>
      )}
    </div>
  );
}
